/* eslint-disable */
import { Inject } from '@angular/core';
import { Router } from '@angular/router';
import { DevstroupeNavigationItem } from '@devstroupe/components/navigation';
import { UserModel } from 'app/models/user.model';
import {
    ENFORCE_FOR_ADMIN_CODE,
    isEnforcedAdminPermissionCode,
} from 'app/core/auth/admin-panel-permissions';

function groupHas(user: UserModel | null | undefined, code: string): boolean {
    return user?.permission_group?.permissions?.some((p) => p.code === code) || false;
}

function userHasPermissionCode(user: UserModel | null | undefined, code: string): boolean {
    if (!user) return false;

    if (user.is_admin) {
        const enforce = groupHas(user, ENFORCE_FOR_ADMIN_CODE);
        if (!enforce) {
            return true;
        }
        if (isEnforcedAdminPermissionCode(code)) {
            return groupHas(user, code);
        }
        return true;
    }

    return groupHas(user, code);
}

// criação de funções para ocultar os itens do menu sando a screens
export const HIDE_IF_SCREEN = (item: HiddenOptions, screen: string) => {
    return !item?.user;
};

//hide if function
export const HIDE_IF_PERMISSION = (item: HiddenOptions, permission_code: string) => {
    if (!item?.user) return false;
    return userHasPermissionCode(item.user, permission_code);
};

// Função para ocultar se o usuário NÃO tiver a permissão
export const HIDE_IF_NO_PERMISSION = (item: HiddenOptions, permission_code: string) => {
    if (!item?.user) return true; // Ocultar se não houver usuário
    return !userHasPermissionCode(item.user, permission_code);
};

/**
 * Ocultar se o usuário NÃO tiver pelo menos UMA das permissões (lógica SOME)
 * Similar ao PermissionGuardSome
 */
export const HIDE_IF_NO_PERMISSION_SOME = (item: HiddenOptions, permission_codes: string | string[]) => {
    if (!item?.user) return true; // Ocultar se não houver usuário

    const permissions = Array.isArray(permission_codes) ? permission_codes : [permission_codes];
    const hasAnyPermission = permissions.some((code) => userHasPermissionCode(item.user, code));

    return !hasAnyPermission;
};

/**
 * Ocultar se o usuário NÃO tiver TODAS as permissões (lógica EVERY)
 * Similar ao PermissionGuardEvery
 */
export const HIDE_IF_NO_PERMISSION_EVERY = (item: HiddenOptions, permission_codes: string | string[]) => {
    if (!item?.user) return true; // Ocultar se não houver usuário

    const permissions = Array.isArray(permission_codes) ? permission_codes : [permission_codes];
    const hasAllPermissions = permissions.every((code) => userHasPermissionCode(item.user, code));

    return !hasAllPermissions;
};

/**
 * Verifica se há pelo menos um filho visível (recursivo)
 * Se nenhum filho estiver visível, o item collapsable deve ser ocultado
 */
const hasVisibleChildren = (item: HiddenOptions, children: DevstroupeNavigationItem[]): boolean => {
    if (!children || children.length === 0) {
        return false;
    }

    return children.some((child) => {
        // Verificar se o filho está oculto
        let isChildHidden = false;

        if (child.hidden) {
            if (typeof child.hidden === 'function') {
                isChildHidden = child.hidden(item);
            } else {
                isChildHidden = child.hidden;
            }
        }

        // Se o filho não está oculto, está visível
        if (!isChildHidden) {
            return true;
        }

        // Se o filho está oculto mas tem filhos, verificar recursivamente
        if (child.children && child.children.length > 0) {
            return hasVisibleChildren(item, child.children);
        }

        return false;
    });
};

export interface HiddenOptions {
    user?: UserModel;
}

/** Usuários não-admin com permissão VIEW para pelo menos um cargo, controla visibilidade do item único "Usuários". */
const USER_VIEW_PERMISSIONS = ['DOCTORS_VIEW', 'NURSES_VIEW', 'NURSING_TECHS_VIEW', 'DRIVERS_VIEW', 'PARAMEDICS_VIEW', 'COMPANY_ADMIN_VIEW'];

export const classicNavigation: DevstroupeNavigationItem[] = [
    {
        id: 'p.home',
        title: 'Home',
        subtitle: '',
        type: 'basic',
        icon: 'heroicons_outline:home',
        link: '/home',
    },
    {
        id: 'p.services',
        title: 'Atendimentos',
        subtitle: '',
        icon: 'heroicons_outline:clipboard-document-list',
        type: 'basic',
        link: '/services',
        hidden: (item: HiddenOptions) => HIDE_IF_NO_PERMISSION(item, 'SERVICE_VIEW'),
    },
    {
        id: 'p.vehicles',
        title: 'Ambulâncias',
        subtitle: '',
        type: 'basic',
        icon: 'heroicons_outline:truck',
        link: '/vehicles',
        hidden: (item: HiddenOptions) => HIDE_IF_NO_PERMISSION(item, 'VEHICLE_VIEW'),
    },
    {
        id: 'p.equipe',
        title: 'Equipe',
        subtitle: '',
        type: 'collapsable',
        icon: 'heroicons_outline:clock',
        hidden: (item: HiddenOptions) =>
            HIDE_IF_NO_PERMISSION(item, 'SHIFT_VIEW') && HIDE_IF_NO_PERMISSION_SOME(item, USER_VIEW_PERMISSIONS),
        children: [
            {
                id: 'p.access_users',
                title: 'Usuários',
                subtitle: '',
                type: 'basic',
                icon: 'heroicons_outline:identification',
                link: '/users',
                hidden: (item: HiddenOptions) => HIDE_IF_NO_PERMISSION_SOME(item, USER_VIEW_PERMISSIONS),
            },
            {
                id: 'p.equipe.schedule',
                title: 'Escala',
                subtitle: '',
                type: 'basic',
                icon: 'heroicons_outline:calendar-days',
                link: '/shifts/schedule',
                hidden: (item: HiddenOptions) => HIDE_IF_NO_PERMISSION(item, 'SHIFT_VIEW'),
            },
            {
                id: 'p.equipe.allocations',
                title: 'Alocação',
                subtitle: '',
                type: 'basic',
                icon: 'heroicons_outline:clock',
                link: '/shifts/allocations',
                hidden: (item: HiddenOptions) => HIDE_IF_NO_PERMISSION(item, 'SHIFT_VIEW'),
            },
        ],
    },
    {
        id: 'p.patients',
        title: 'Pacientes',
        subtitle: '',
        type: 'basic',
        icon: 'heroicons_outline:user-group',
        link: '/patients',
        hidden: (item: HiddenOptions) => HIDE_IF_NO_PERMISSION(item, 'PATIENT_VIEW'),
    },
    {
        id: 'p.customer',
        title: 'Clientes',
        subtitle: '',
        type: 'basic',
        icon: 'heroicons_outline:users',
        link: '/customer',
    },
    {
        id: 'p.pharmacy',
        title: 'Estoque',
        subtitle: '',
        type: 'basic',
        icon: 'heroicons_outline:archive-box',
        link: '/pharmacy',
        hidden: (item: HiddenOptions) => HIDE_IF_NO_PERMISSION(item, 'SUPPLY_VIEW'),
    },
/*
    {
        id: 'p.cabins',
        title: 'Cabines',
        subtitle: '',
        type: 'basic',
        icon: 'heroicons_outline:building-storefront',
        link: '/cabins',
        hidden: () => true, // Ocultar Cabines
    },
*/
    {
        id: 'p.tickets',
        title: 'Tickets',
        subtitle: '',
        type: 'basic',
        icon: 'heroicons_outline:ticket',
        link: '/tickets',
        hidden: (item: HiddenOptions) => HIDE_IF_NO_PERMISSION(item, 'TICKET_VIEW'),
    },
    {
        id: 'p.dashboards',
        title: 'Relatórios',
        subtitle: '',
        type: 'collapsable',
        icon: 'heroicons_outline:chart-bar-square',
        children: [
            {
                id: 'p.reports_service',
                title: 'Atendimentos',
                subtitle: '',
                type: 'basic',
                icon: 'heroicons_outline:clipboard-document-list',
                link: '/reports/service',
                hidden: (item: HiddenOptions) => HIDE_IF_NO_PERMISSION(item, 'SERVICE_REPORT_SERVICE_VIEW'),
            },
            {
                id: 'p.reports_fleet',
                title: 'Frota',
                subtitle: '',
                type: 'basic',
                icon: 'heroicons_outline:truck',
                link: '/reports/fleet',
                hidden: (item: HiddenOptions) => HIDE_IF_NO_PERMISSION(item, 'SERVICE_REPORT_FLEET_VIEW'),
            },
        ],
    },
];

/** Menu do portal do solicitante (empresas requester). */
export const requesterNavigation: DevstroupeNavigationItem[] = [
    {
        id: 'req.home',
        title: 'Início',
        subtitle: '',
        type: 'basic',
        icon: 'heroicons_outline:home',
        link: '/requester/home',
    },
    {
        id: 'req.services',
        title: 'Atendimentos',
        subtitle: '',
        type: 'basic',
        icon: 'heroicons_outline:clipboard-document-list',
        link: '/requester/services',
    },
];

/**
 * Menu do Painel Admin — telas globais do sistema (não filtradas por empresa).
 * Acesso restrito a usuários com is_admin=true (ver AdminGuard).
 */
export const adminNavigation: DevstroupeNavigationItem[] = [
    {
        id: 'admin.kpis',
        title: 'Início',
        subtitle: '',
        type: 'basic',
        icon: 'heroicons_outline:squares-2x2',
        link: '/admin/home',
        hidden: (item: HiddenOptions) => HIDE_IF_NO_PERMISSION(item, 'ADMIN_HOME_VIEW'),
    },
    {
        id: 'admin.stakeholders',
        title: 'Stakeholders',
        subtitle: '',
        type: 'collapsable',
        icon: 'heroicons_outline:building-office-2',
        hidden: (item: HiddenOptions) =>
            HIDE_IF_NO_PERMISSION(item, 'ADMIN_COMPANY_VIEW') &&
            HIDE_IF_NO_PERMISSION(item, 'ADMIN_REQUESTER_VIEW') &&
            HIDE_IF_NO_PERMISSION(item, 'ADMIN_PROFESSIONAL_VIEW') &&
            HIDE_IF_NO_PERMISSION(item, 'ADMIN_PROVIDER_VIEW') &&
            HIDE_IF_NO_PERMISSION(item, 'ADMIN_REGULATOR_VIEW'),
        children: [
            {
                id: 'admin.companies',
                title: 'Parceiros',
                subtitle: '',
                type: 'basic',
                icon: 'heroicons_outline:building-office-2',
                link: '/admin/access/companies',
                hidden: (item: HiddenOptions) => HIDE_IF_NO_PERMISSION(item, 'ADMIN_COMPANY_VIEW'),
            },
            {
                id: 'admin.requesters',
                title: 'Solicitantes',
                subtitle: '',
                type: 'basic',
                icon: 'heroicons_outline:building-library',
                link: '/admin/access/requesters',
                hidden: (item: HiddenOptions) => HIDE_IF_NO_PERMISSION(item, 'ADMIN_REQUESTER_VIEW'),
            },
            {
                id: 'admin.professionals',
                title: 'Profissionais',
                subtitle: '',
                type: 'basic',
                icon: 'heroicons_outline:user-minus',
                link: '/admin/access/users-no-company',
                hidden: (item: HiddenOptions) => HIDE_IF_NO_PERMISSION(item, 'ADMIN_PROFESSIONAL_VIEW'),
            },
            {
                id: 'admin.providers',
                title: 'Fornecedores',
                subtitle: '',
                type: 'basic',
                icon: 'heroicons_outline:truck',
                link: '/admin/access/providers',
                hidden: (item: HiddenOptions) => HIDE_IF_NO_PERMISSION(item, 'ADMIN_PROVIDER_VIEW'),
            },
            {
                id: 'admin.regulators',
                title: 'Reguladores',
                subtitle: '',
                type: 'basic',
                icon: 'heroicons_outline:user-group',
                link: '/admin/access/regulators',
                hidden: (item: HiddenOptions) => HIDE_IF_NO_PERMISSION(item, 'ADMIN_REGULATOR_VIEW'),
            },
        ],
    },
    {
        id: 'admin.seguranca',
        title: 'Segurança',
        subtitle: '',
        type: 'collapsable',
        icon: 'heroicons_outline:shield-check',
        hidden: (item: HiddenOptions) =>
            HIDE_IF_NO_PERMISSION(item, 'ADMIN_ADMIN_USER_VIEW') &&
            HIDE_IF_NO_PERMISSION(item, 'ADMIN_PERMISSION_GROUP_VIEW') &&
            HIDE_IF_NO_PERMISSION(item, 'ADMIN_API_KEY_VIEW') &&
            HIDE_IF_NO_PERMISSION(item, 'ADMIN_VEHICLE_PLAN_VIEW'),
        children: [
            {
                id: 'admin.admins',
                title: 'Admin',
                subtitle: '',
                type: 'basic',
                icon: 'heroicons_outline:shield-check',
                link: '/admin/access/admins',
                hidden: (item: HiddenOptions) => HIDE_IF_NO_PERMISSION(item, 'ADMIN_ADMIN_USER_VIEW'),
            },
            {
                id: 'admin.permission_groups',
                title: 'Permissões',
                subtitle: '',
                type: 'basic',
                icon: 'heroicons_outline:shield-check',
                link: '/admin/access/permission-groups',
                hidden: (item: HiddenOptions) => HIDE_IF_NO_PERMISSION(item, 'ADMIN_PERMISSION_GROUP_VIEW'),
            },
            {
                id: 'admin.api_keys',
                title: 'API Keys',
                subtitle: '',
                type: 'basic',
                icon: 'heroicons_outline:key',
                link: '/admin/access/api-keys',
                hidden: (item: HiddenOptions) => HIDE_IF_NO_PERMISSION(item, 'ADMIN_API_KEY_VIEW'),
            },
            {
                id: 'admin.vehicle_plans',
                title: 'Planos',
                subtitle: '',
                type: 'basic',
                icon: 'heroicons_outline:truck',
                link: '/admin/vehicle-plans',
                hidden: (item: HiddenOptions) => HIDE_IF_NO_PERMISSION(item, 'ADMIN_VEHICLE_PLAN_VIEW'),
            },
        ],
    },
    {
        id: 'admin.atendimentos',
        title: 'Atendimentos',
        subtitle: '',
        type: 'collapsable',
        icon: 'heroicons_outline:clipboard-document-list',
        hidden: (item: HiddenOptions) =>
            HIDE_IF_NO_PERMISSION(item, 'ADMIN_TYPE_SERVICE_VIEW') &&
            HIDE_IF_NO_PERMISSION(item, 'ADMIN_MEDICAL_RECORD_VIEW') &&
            HIDE_IF_NO_PERMISSION(item, 'ADMIN_PERSONAL_BACKGROUND_VIEW') &&
            HIDE_IF_NO_PERMISSION(item, 'ADMIN_SCALE_VIEW') &&
            HIDE_IF_NO_PERMISSION(item, 'ADMIN_HEALTH_PLAN_VIEW') &&
            HIDE_IF_NO_PERMISSION(item, 'ADMIN_CNES_VIEW') &&
            HIDE_IF_NO_PERMISSION(item, 'ADMIN_SUPPLY_CATALOG_VIEW') &&
            HIDE_IF_NO_PERMISSION(item, 'ADMIN_PROMPT_VIEW'),
        children: [
            {
                id: 'admin.type_service',
                title: 'Tipos',
                subtitle: '',
                type: 'basic',
                icon: 'heroicons_outline:clipboard-document-list',
                link: '/admin/services/type_service',
                hidden: (item: HiddenOptions) => HIDE_IF_NO_PERMISSION(item, 'ADMIN_TYPE_SERVICE_VIEW'),
            },
            {
                id: 'admin.medical_record',
                title: 'Fichas',
                subtitle: '',
                type: 'basic',
                icon: 'heroicons_outline:document-text',
                link: '/admin/services/medical_record',
                hidden: (item: HiddenOptions) => HIDE_IF_NO_PERMISSION(item, 'ADMIN_MEDICAL_RECORD_VIEW'),
            },
            {
                id: 'admin.personal_background',
                title: 'Antecedentes Pessoais',
                subtitle: '',
                type: 'basic',
                icon: 'heroicons_outline:document-text',
                link: '/admin/services/personal_background',
                hidden: (item: HiddenOptions) => HIDE_IF_NO_PERMISSION(item, 'ADMIN_PERSONAL_BACKGROUND_VIEW'),
            },
            {
                id: 'admin.scale',
                title: 'Avaliação Neurológica',
                subtitle: '',
                type: 'basic',
                icon: 'heroicons_outline:beaker',
                link: '/admin/services/scale',
                hidden: (item: HiddenOptions) => HIDE_IF_NO_PERMISSION(item, 'ADMIN_SCALE_VIEW'),
            },
            {
                id: 'admin.health_plan',
                title: 'Planos de Saúde',
                subtitle: '',
                type: 'basic',
                icon: 'heroicons_outline:document-text',
                link: '/admin/services/health_plan',
                hidden: (item: HiddenOptions) => HIDE_IF_NO_PERMISSION(item, 'ADMIN_HEALTH_PLAN_VIEW'),
            },
            {
                id: 'admin.cnes_establishment',
                title: 'Estabelecimentos',
                subtitle: '',
                type: 'basic',
                icon: 'heroicons_outline:building-office-2',
                link: '/admin/services/cnes_establishment',
                hidden: (item: HiddenOptions) => HIDE_IF_NO_PERMISSION(item, 'ADMIN_CNES_VIEW'),
            },
            {
                id: 'admin.supply',
                title: 'Insumos',
                subtitle: '',
                type: 'basic',
                icon: 'heroicons_outline:archive-box',
                link: '/admin/supply',
                hidden: (item: HiddenOptions) => HIDE_IF_NO_PERMISSION(item, 'ADMIN_SUPPLY_CATALOG_VIEW'),
            },
            {
                id: 'admin.prompts',
                title: 'Prompts IA',
                subtitle: '',
                type: 'basic',
                icon: 'heroicons_outline:code-bracket-square',
                link: '/admin/prompts',
                hidden: (item: HiddenOptions) => HIDE_IF_NO_PERMISSION(item, 'ADMIN_PROMPT_VIEW'),
            },
        ],
    },
    {
        id: 'admin.equipamentos',
        title: 'Equipamentos',
        subtitle: '',
        type: 'basic',
        icon: 'heroicons_outline:wrench-screwdriver',
        link: '/admin/equipment',
        hidden: (item: HiddenOptions) => HIDE_IF_NO_PERMISSION(item, 'ADMIN_EQUIPMENT_VIEW'),
    },
    {
        id: 'admin.integrations',
        title: 'Integrações',
        subtitle: '',
        type: 'basic',
        icon: 'heroicons_outline:code-bracket-square',
        link: '/admin/integrations',
        hidden: (item: HiddenOptions) => HIDE_IF_NO_PERMISSION(item, 'ADMIN_INTEGRATION_VIEW'),
    },
    {
        id: 'admin.crm',
        title: 'Ligações',
        subtitle: '',
        type: 'basic',
        icon: 'heroicons_outline:phone',
        link: '/admin/crm/calls',
        hidden: (item: HiddenOptions) => HIDE_IF_NO_PERMISSION(item, 'ADMIN_CRM_CALLS_VIEW'),
    },
    {
        id: 'admin.news',
        title: 'Comunicações',
        subtitle: '',
        type: 'basic',
        icon: 'heroicons_outline:newspaper',
        link: '/admin/news',
        hidden: (item: HiddenOptions) => HIDE_IF_NO_PERMISSION(item, 'ADMIN_NEWS_VIEW'),
    },
    {
        id: 'admin.pricing',
        title: 'Pricing',
        subtitle: '',
        type: 'basic',
        icon: 'heroicons_outline:banknotes',
        link: '/admin/pricing',
        hidden: (item: HiddenOptions) => HIDE_IF_NO_PERMISSION(item, 'ADMIN_PRICING_VIEW'),
    },
];

export const compactNavigation: DevstroupeNavigationItem[] = [
    {
        id: 'example',
        title: 'Example',
        type: 'basic',
        icon: 'heroicons_outline:chart-pie',
        link: '/example',
    },
];
export const futuristicNavigation: DevstroupeNavigationItem[] = [
    {
        id: 'example',
        title: 'Example',
        type: 'basic',
        icon: 'heroicons_outline:chart-pie',
        link: '/example',
    },
];
export const horizontalNavigation: DevstroupeNavigationItem[] = [
    {
        id: 'example',
        title: 'Example',
        type: 'basic',
        icon: 'heroicons_outline:chart-pie',
        link: '/example',
    },
];
